import React, { FC, ReactElement } from 'react';
import Head from 'next/head';
import Loading, { LoadingConfig } from '@/static/layout/loading';
import Error from '@/static/layout/error';
import useApi from '@/static/hook/useApi';
import useClient from '@/static/hook/useClient';
import { OverseaBridge } from '@/static/hook/useBridge';
import ShareHead, { ShareHeadProps } from '@/static/layout/shareHead';

interface Props {
  title: string;
  loading?: boolean;
  loadingConfig?: LoadingConfig;
  needInApp?: boolean;
  needLogin?: {
    status: boolean;
    ksBridge?: OverseaBridge;
  };
  disableIos?: boolean;
  abnormal?: {
    status: boolean;
    tip: string;
  };
  api?: string;
  share?: ShareHeadProps;
  errorCollect?: boolean;
  children: ReactElement;
}

/**
 * 通用的预渲染Layout
 * @param title 必须携带的参数，取消默认的title，因为该title要在客户端获取，如果有默认title在服务端渲染，会发生闪烁
 * @param loading 是否处于loading状态
 * @param loadingConfig loading相关属性
 * @param needInApp 是否只有在App中才能打开
 * @param needLogin 在端内打开页面时，是否需要登陆，
 * @param abnormal  异常提示，包括状态和文案
 * @param api 一条api请求，该请求在html下载完成后便开始预请求，方式为get，并对登陆状态进行捕获
 * @param share 是否支持站外分享，以及相应配置，注意不要用多语言等变量，head无法修改
 * @param errorCollect 是否通过写死在html中的脚本向雷达报错，处理cdn异常的情况
 * @param children
 * @constructor
 */
const StaticLayout: FC<Props> = ({
  title,
  loading = false,
  loadingConfig,
  needInApp = false,
  needLogin,
  abnormal,
  api = '',
  share,
  errorCollect,
  children,
}) => {
  const { notLogin } = useApi(api);
  const { isInSnack } = useClient();

  let Children = (
    <>
      {loading ? <Loading {...loadingConfig} /> : null}
      {children}
    </>
  );

  if (isInSnack && needLogin && notLogin) {
    needLogin.ksBridge &&
      needLogin.ksBridge.login({
        success: () => window.location.reload(),
      });
    Children = <Error tip={'请登陆APP'} />;
  }

  if (needInApp && !isInSnack) {
    Children = <Error tip={'请在app中打开该网页'} />;
  }

  // if (disableIos && isIos) {
  //   Children = <></>;
  //   window.location.href = '/one/common/404?type=device';
  // }

  if (abnormal && abnormal.status) {
    Children = <Error tip={abnormal.tip} />;
  }

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta httpEquiv="Content-Type" content="text/html;charset=utf-8" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,viewport-fit=cover,maximum-scale=1.0,minimum-scale=1.0,user-scalable=no"
        />
        <meta name="keywords" content="SnackVideo,snack,video" />
        <meta name="format-detection" content="telephone=no" />
        <meta httpEquiv="X-UA-Compatible" content="IE=Edge,chrome=1" />
        <meta httpEquiv="x-dns-prefetch-control" content="on" />
        <meta name="google-site-verification" content="ZCM-gQ7ZhiE_7mgOAp-k0uUz88gxmqKFNY9nv87ViOA" />

        {errorCollect && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html:
                  '!function(e){"function"==typeof define&&define.amd?define(e):e()}(function(){"use strict";!function(){function r(){return(new Date).getTime()}function a(){return p&&p.timing&&p.timing.navigationStart}var e=r(),i="script",u="resource",n="error",t="console",o="count",c=[],s=[],d=null,f=null,l=-1<navigator.userAgent.indexOf(" Yoda/"),p="undefined"!=typeof window?window.performance:null,m=console.error;window.yodaCollectErrorDataDestroy=function(){window.removeEventListener("error",_,!0),window.removeEventListener("unhandledrejection",w,!1),console.error=m,f=d=null};var g=function(e){var n=p&&"function"==typeof p.getEntriesByType&&p.getEntriesByType("resource")||[];C(y({key:o,value:{name:"count_load",resources:n.map(function(e){return{name:e.name,startTime:e.startTime,duration:e.duration,initiatorType:e.initiatorType,transferSize:e.transferSize}}),msg:n.length,duration:r()-a()}}))},_=function(e){console.log(e);var n,t=(o=e).message||null!=o.lineno,o=e.error;t?(n={error_type:i,name:"js_error",msg:e.message||e.reason,stack:o?o.stack:null,file:e.filename,line:e.lineno,col:e.colno,duration:r()-a()},h(n)):(n=e.target.href,C(y({key:u,value:{name:"resource_error",file:n,msg:n&&n.split(".").pop(),stack:v(e.path||e.composedPath&&e.composedPath()),duration:r()-a()}})))},v=function(e){return Array.isArray(e)?e.map(function(e){var n=e.tagName,t=e.id,e=e.className,n=(n=void 0===n?"":n).toLowerCase();return t&&(n+="#"+t),e&&(n+=e.split(" ").map(function(e){return"."+e}).join("")),n}).filter(function(e){return e}).join(","):""};function x(e){return f||(f={},navigator.userAgent.split(" ").forEach(function(e){e=e.split("/");f[e[0]]=e[1]})),f[e]||f}var y=function(e){var n=e.key,t=e.value,o=t.name,r=t.file,a=t.error_type,i=t.msg;return{key:"event",value:{duration:t.duration},dimension:{name:o,category:"debug-log",event_type:n||a,src:r,result_type:"",message:i,extra_info:e.value||{},yoda_version:x("Yoda")||""}}},w=function(e){e=e.reason&&e.reason.message||"",e={error_type:i,name:"js_error_promise",msg:e,duration:r()-a()};k(e)},k=function(e){C(y({key:n,value:e}))},h=function(e){0<=O(e)||(c.push(e),k(e),setTimeout(function(){c.splice(O(e),1)},2e3))},O=function(n){return c.findIndex(function(e){return e.file===n.file&&e.line===n.line&&e.col===n.col})},N={unknown:0,none:1,wifi:2,"4g":3,"3g":4,"2g":5,"slow-2g":5},T=0;function A(e){if(!d){d={};for(var n,t=document.cookie.split("; "),o=0;o<t.length;o++)n=t[o].split("="),d[n[0]]=decodeURIComponent(n[1]||"")}return d[e]||""}function L(){var e="";return(e=A("session_id")||A("sid"))?e:"kibtlog_"+function(e){void 0===e&&(e=!0);var t=(new Date).getTime();return"undefined"!=typeof performance&&"function"==typeof performance.now&&(t+=performance.now()),(e?"xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx":"xxxxxxxx_xxxx_4xxx_yxxx_xxxxxxxxxxxx").replace(/[xy]/g,function(e){var n=(t+16*Math.random())%16|0;return t=Math.floor(t/16),("x"===e?n:3&n|8).toString(16)})}(!1)}function b(e,n,t){if(void 0===t&&(t=" "),(n-=e.length)<=0)return e;for(var o="";n;)1&n&&(o+=t),n>>=1,t+=t;return o+e}function B(e){var n=e.key,t=e.value,o=e.dimension;o.extra_info.timestamp=r(),o.extra_info.navigation_start_time=a(),o.extra_info=JSON.stringify(o.extra_info),e={identity_package:{device_id:A("did")||A("_did"),global_id:A("egid"),user_id:A("userId")||A("userName")},app_package:{product:"KWAI_BULLDOG",language:navigator.language,platform:8,container:"H5",package_name:"KWAI_BULLDOG",product_name:"KWAI_BULLDOG",version_name:A("appver")||"",channel:"",version_code:0},device_package:{os_version:"",model:"",ua:navigator.userAgent},need_encrypt:!1,network_package:{type:function(){try{var e=navigator.connection||navigator.mozConnection||navigator.webkitConnection;if(!e)return N.unknown;var n=e.type,t=e.effectiveType;return N[n]||N[t]||N.unknown}catch(e){return N.unknown}}()},experiment:[],sdk_version:x("Yoda")||""},T++,o={common:e,logs:{event:[{client_timestamp:(new Date).valueOf(),client_increment_id:T,session_id:L(),time_zone:j,stat_package:{custom_stat_event:{key:"radar_log",value:JSON.stringify({url_package:{page:location.href,page_type:2},refer_url_package:{page:document.referrer,page_type:2},project_id:"21117c1cdd",radar_session_id:function(){for(var e=1e9*Math.random()>>>0,n=[],t=0;t<7;t++)n.push("0123456789ABCDEF".charAt(16*Math.random()));return e+n.join("")}(),h5_extra_attr:JSON.stringify({app_version_name:A("appver"),url:location.href,hash:location.hash,county_name:{country:A("countryInfo")||"UNKNOWN",bucket:A("bucket")||"UNKNOWN"}}),data:[{key:n,value:t,dimension:o}]})}}}]}},I({url:"https://logsdk.kwai-pro.com/rest/wd/common/log/collect/radar",data:M(o),timeout:3e4,callback:function(){}})}function D(e){var n=e.url,t=e.data,o=e.timeout,r=e.callback,a=new XMLHttpRequest;a.open("POST",n),o&&(a.timeout=o),a.onload=function(){var e;(a.status<200||300<=a.status&&304!==a.status)&&(e=a.response||a.status||"unknonw error"),r&&r(e)},a.ontimeout=a.onerror=function(e){r&&r(e)},a.send(t)}var E,S,j=(E=(new Date).getTimezoneOffset()/60)<=0?"GMT+"+b(-E+"",2,"0")+":00":"GMT-"+b(E+"",2,"0")+":00",I=function(n){var t=n.url,o=n.data,r=n.timeout,n=n.callback;try{"undefined"!=typeof window&&void 0!==window.navigator&&"function"==typeof window.navigator.sendBeacon?navigator.sendBeacon(t,o):D({url:t,data:o,timeout:r,callback:n})}catch(e){D({url:t,data:o,timeout:r,callback:n})}},M=function(e){var n,t=new FormData;for(n in e)e.hasOwnProperty(n)&&t.append(n,JSON.stringify(e[n]));return t},C=function(e){!l||window.__yodaBridge__?Array.isArray(e)?e.map(function(e){C(e)}):B(e):U(e)},U=function(e){s.push(e)};S=window.__yodaBridgeReady__,window.__yodaBridgeReady__=function(){S&&"function"==typeof S&&S(),C(s)},window.addEventListener("error",_,!0),window.addEventListener("unhandledrejection",w,!1),window.addEventListener("load",g,!1),console.error=function(){m&&"function"==typeof m&&m(arguments),C(y({key:t,value:{msg:JSON.stringify(Array.prototype.slice.apply(arguments)),name:"console_error",duration:r()-a()}}))},C(y({key:o,value:{name:"count_inject",duration:r()-e}}))}()});',
              }}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: `;(function() {
                  if ((document.cookie || '').indexOf('1627985338192=1') > -1) {
                    return;
                  }
                  document.cookie = '1627985338192=1; path=/; ; max-age=3600';
                  var date = new Date();
                  var now = date.getTime();
                  [
                    'https://aws-static.kwai.net/udata/pkg/proweb/cdntest/13.png',
                    'https://ak-static.kwai.net/udata/pkg/proweb/cdntest/13.png',
                    'https://tx-static.kwai.net/udata/pkg/proweb/cdntest/13.png',
                    'https://cdn-static.kwai.net/kos/s101/nlav11312/other/cdntest/13.png'
                  ].forEach(function(url){
                    setTimeout(function() {
                      var link = document.createElement('link');
                      link.rel = 'preload';
                      link.href= url+'?_v=' + now;
                      link.as ='image';
                      document.body.appendChild(link);
                    }, 5000 + Math.random() * 5000);
                  });
                })();`,
              }}
            ></script>
          </>
        )}

        {share && <ShareHead {...share} />}

        {api && <link rel="preload" href={api} as="fetch" crossOrigin="anonymous" />}
        <link rel="dns-prefetch" href="//m.snackvideo.com" />
        <link rel="dns-prefetch" href="//d3qrssutfqv0j9.cloudfront.net" />
        <link
          rel="shortcut icon"
          type="image/ico"
          href="//d3qrssutfqv0j9.cloudfront.net/udata/pkg/proweb/share/snack_share_logo_150.png"
        />

        <script src="//d3qrssutfqv0j9.cloudfront.net/udata/pkg/proweb/basic/rem_720_new.js" />
        {process.env.eruda === 'on' && <script src="//aws-static.kwai.net/udata/pkg/proweb/common/eruda.min.js" />}
        {process.env.eruda === 'on' && <script dangerouslySetInnerHTML={{ __html: 'eruda.init();' }} />}
      </Head>
      {Children}
    </>
  );
};

export default StaticLayout;
